<script setup>
// TODO: Change file name to LayoutMap (or create LayoutMap) add to layout folder
import { ref, computed, watch, onMounted } from 'vue';
import { useMapUIStore } from "@/stores/MapUIStore";
import { useMapStore } from "@/stores/MapStore";
import { useUserStore } from "@/stores/UserStore";
import { useMapAdStore } from "@/stores/MapAdStore";
import { useMediaQuery } from '@/composables/useMediaQuery';
import { useMapAutoRefresh } from '@/composables/useMapAutoRefresh';
import { useMapCustomLayerStore } from '@/stores/MapCustomLayerStore';
import MapSidebar from '@/components/MapSidebar.vue';
import MapName from '@/components/MapName.vue'
import MapRegionalNavigation from '@/components/MapRegionalNavigation.vue'
import MapControlsPrimary from '@/components/MapControlsPrimary.vue'
import MapControlsSecondary from '@/components/MapControlsSecondary.vue'
import MapUpgradeButton from '@/components/MapUpgradeButton.vue'
import MapLayerDashboards from '@/components/MapLayerDashboards.vue'
import MapTimeline from '@/components/MapTimeline.vue'
import MapModelSelector from '@/components/MapModelSelector.vue'
import MapUserProfileSelector from '@/components/MapUserProfileSelector.vue'
import MapPrecipAccumPeriodSelector from '@/components/MapPrecipAccumPeriodSelector.vue'
import MapLegend from '@/components/MapLegend.vue'
import MapOverlayLegendDSAT from '@/components/MapOverlayLegendDSAT.vue'
import SheetsContainer from '@/components/SheetsContainer.vue'
import MapMessages from "@/components/MapMessages.vue";
import MapAds from '@/components/MapAds.vue';
import MapDrawingTools from '@/components/MapDrawingTools.vue';
import MapLegacyInfoWindowInjects from '@/components/MapLegacyInfoWindowInjects.vue';
import MapToast from '@/components/MapToast.vue';
// import MobileNav from '@/components/MobileNav.vue';
import useMapConfigurationGlobals from '@/composables/useMapConfigurationGlobals';
import useAdConfigurationGlobals from '@/composables/useAdConfigurationGlobals';

const { $MAP_AUTO_REFRESH, $MAP_DASHBOARD, $DRAWING_TOOLS, $MAP_CUSTOM_LAYERS } = useMapConfigurationGlobals();  
const { $ADS_ALLOWED } = useAdConfigurationGlobals();   

window.$JWX = {}
window.$JWX.ready = false;

const mapStore = useMapStore();
const mapUIStore = useMapUIStore();
const userStore = useUserStore();
const mapAdStore = useMapAdStore();
const mapCustomLayerStore = useMapCustomLayerStore();

const { start } = useMapAutoRefresh();
const isDesktop = useMediaQuery('(min-width: 840px)');
const isSmallDevice = useMediaQuery('(max-width: 839px)');
const systemThemeIsDark = useMediaQuery('(prefers-color-scheme: dark)');

const mapReady = ref(false);

mapUIStore.updateThemes();
mapUIStore.toggleLegend();

const showModelSelector = computed(() => mapReady.value && mapStore.mapMode.toLowerCase() === 'fx' && mapStore.state.forecastSelectorNeeded);
const showProfileSelector = computed(() =>  mapReady.value && mapStore.state.useProfileId);
const showPrecipAccumPeriodSelector = computed(() => mapReady.value && mapStore.state.obsOverlays.has('precip-accumulation'));
const showUpgradeButton = computed(() => userStore.memberLevel < 49 && mapStore.state.obsOverlays.has('markers'));
const showMapMessages = computed(() => userStore.messages && userStore.messages.length > 0 && parseInt(userStore.nonTrialMemberLevel) < 49);
const showLayerDashboards = computed(() => mapReady.value && isDesktop.value && $MAP_DASHBOARD && (mapStore.state.obsOverlays.has('watches-warnings') || mapStore.state.obsOverlays.has('dsat-operating-condition') || mapStore.state.obsOverlays.has('lightning')));
const showDSATOverlayLegend = computed(() => mapReady.value && mapStore.state.obsOverlays.has('dsat-operating-condition'));
const showDrawingTools = computed(() => mapReady.value && $DRAWING_TOOLS);
const showMapAd = computed(() => $ADS_ALLOWED && userStore.showAds && isDesktop);

onMounted(() => {  
  mapUIStore.hasRegionalNavigation(mapUIStore.showRegionalNavigation);

  mapUIStore.$subscribe(() => {
    mapUIStore.updateThemes(systemThemeIsDark.value);
    mapUIStore.toggleLegend();
  });

  window.$JWX.ready = true;
  window.$EventBus.dispatchEvent('jwx-ready');
  
  window.$EventBus.addEventListener('map-ready', (e) => {
    window.$JWX.map = e.detail.map;

    watch(systemThemeIsDark, () => {
      mapUIStore.updateThemes(systemThemeIsDark.value);

      if (mapStore.state.mapBackground.usesThemes) {
        mapStore.mapSettingsUpdate();
      }
    },{ immediate: true });

    (async () => {
      await userStore.getProfiles(true);
    })();  

    google.maps.event.addListener(window.$JWX.map, 'idle', function () {    
      mapReady.value = true;
      mapStore.loadInitialView();
      
      if (showMapAd.value) mapAdStore.refreshAd();

      if ($MAP_CUSTOM_LAYERS) {
        mapCustomLayerStore.setMapVar(window.$JWX.map);

        (async () => {
          await mapCustomLayerStore.getCustomLayers();
        })();
      }
    });

    if ($MAP_AUTO_REFRESH) {
      start();
    }
  });

});

</script>

<template>
  <MapMessages v-if="showMapMessages" />
  <MapSidebar v-if="mapReady" />
  <MapRegionalNavigation v-if="mapReady && mapUIStore.showRegionalNavigation" />
  <div class="jwx-map__header">
    <MapName />
  </div>
  <MapControlsPrimary />
  <div v-if="isDesktop" class="jwx-map__shared">
    <MapAds v-if="showMapAd" />
    <MapControlsSecondary />
  </div>
  <MapUpgradeButton v-if="showUpgradeButton" :isSmallDevice="isSmallDevice" />
  <MapLayerDashboards v-show="showLayerDashboards" />
  <Suspense>
    <MapModelSelector v-if="showModelSelector" />
  </Suspense>
  <Suspense>
    <MapUserProfileSelector v-if="showProfileSelector" />
  </Suspense>
  <Suspense>
    <MapTimeline v-if="mapReady" :isSmallDevice="isSmallDevice" />
  </Suspense>

  <MapPrecipAccumPeriodSelector v-if="showPrecipAccumPeriodSelector" />
  <MapDrawingTools v-if="showDrawingTools" :isSmallDevice="isSmallDevice"/>
  <MapLegend />
  <MapOverlayLegendDSAT v-if="showDSATOverlayLegend" />
  <SheetsContainer />
  <MapLegacyInfoWindowInjects />
  <MapToast v-if="mapReady" />
  <div v-if="isSmallDevice" class="jwx-map__bottom-container"></div>
  <!-- <MobileNav /> -->
</template>
